import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ViewImage from 'pages/modal/ViewImage';
import classnames from 'classnames';
import styles from 'styles/components/common/forms/MediaFile.module.scss';
import useModal from 'hooks/useModal';

type TProps = {
  name?: string;
  files?: File[];
  multiple?: boolean;
  maxLength?: number;
  limit?: number;
  inputId: string;
  accept?: string;
  acceptVideo?: boolean;
  readOnly?: boolean;
  className?: string;
  onDelete?: (index: number) => void;
  onChange?: (file: File[]) => void;
};

const FormMediaFile = forwardRef((props: TProps, ref) => {
  useImperativeHandle(ref, () => ({
    resetFile,
  }));

  const acceptExt = props.accept
    ? props.accept
    : `jpg, jpeg, png${props.acceptVideo ? ', mp4' : ''}`;
  const acceptExtArray = acceptExt.split(',').map((ext) => ext.trim());
  const fileLimit = props.limit || 10;
  const fileLength = props.maxLength ? props.maxLength : 1;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageURL, setImageURL] = useState<string[]>([]);
  const [videoURL, setVideoURL] = useState<string[]>([]);
  const [fileList, setFileList] = useState<File[]>([]);

  const { openModal } = useModal();

  useEffect(() => {
    loadFile(props.files || [], false);
  }, [props.files]);

  const getStyles = (condition: boolean | undefined, className: string) => {
    return condition && !!className ? styles[className] : '';
  };

  const resetFile = (fileType?: string, index?: number) => {
    const dataTransfer = new DataTransfer();

    switch (fileType) {
      case 'video':
        setVideoURL((prev) => prev.filter((_, i) => i !== index));
        break;
      case 'image':
        setImageURL((prev) => prev.filter((_, i) => i !== index));
        break;
      default:
        setVideoURL([]);
        setImageURL([]);
        break;
    }

    if (index !== undefined) {
      const fileArray = props.files ? Array.from(props.files) : [];

      fileArray.splice(index, 1);
      fileArray.forEach((file) => dataTransfer.items.add(file));
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    props.onChange && props.onChange(Array.from(dataTransfer.files ?? []));
  };

  const deleteFile = (fileType: string, index: number) =>
    openModal({
      isConfirm: true,
      text: `해당 ${
        fileType === 'video' ? '영상' : '사진'
      }을 삭제하시겠습니까?`,
      onSubmit: () => {
        const type = fileList[index].type.split(':');

        if (type[0] === 'fileid') {
          props.onDelete && props.onDelete(Number(type[1]));
        }
        resetFile(fileType, index);
      },
    });

  const getFileExts = (files: File[]) => {
    const fileNames = files.map((file) => file.name);

    return fileNames.map(
      (name) => name.split('.').pop()?.toLowerCase() || null
    );
  };

  const loadFile = (files: File[], isChange: boolean) => {
    const fileExts = getFileExts(files);
    const getPath = (file: File) => {
      return file.type.includes('fileid')
        ? file.name
        : URL.createObjectURL(file);
    };

    fileExts.forEach((ext, index) => {
      switch (ext) {
        case 'mp4':
          setVideoURL((prev) => [
            ...prev,
            ...(prev && prev[index] ? [] : [getPath(files[index])]),
          ]);
          break;
        default:
          setImageURL((prev) => [
            ...prev,
            ...(prev && prev[index] ? [] : [getPath(files[index])]),
          ]);
          break;
      }
    });

    const dataTransfer = new DataTransfer();
    const fileArray = files ? (isChange ? [...fileList, ...files] : files) : [];

    fileArray.forEach((file) => dataTransfer.items.add(file));
    setFileList(Array.from(dataTransfer.files ?? []));
    isChange &&
      props.onChange &&
      props.onChange(Array.from(dataTransfer.files));
  };

  const changeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      resetFile();
      return;
    }

    const files = Array.from(event.target.files);
    const fileSize = files.map((file) => file.size / 1024 / 1024);
    const fileExts = getFileExts(files);
    const fileUnit = props.acceptVideo ? '개' : '장';

    if (fileList.length + files.length > fileLength) {
      openModal({
        text: `최대 ${fileLength}${fileUnit}까지만 업로드 가능합니다.`,
      });
      return;
    }

    if (fileExts.find((ext) => !acceptExtArray.includes(ext || ''))) {
      openModal({
        text: `${acceptExt} 파일 형식만 업로드 가능합니다.\n (최대 ${fileLength}${fileUnit}, ${fileLimit}MB 이하)`,
      });
      return;
    }

    if (fileSize.find((size) => size > fileLimit)) {
      openModal({
        text: `${fileLimit}MB 이하 파일만 업로드 가능합니다.`,
      });
      return;
    }

    loadFile(files, true);
  };

  const viewFile = (path: string) => {
    openModal({
      element: <ViewImage path={path} />,
    });
  };

  return (
    <>
      <div
        className={classnames(
          styles.media_uploader,
          getStyles(props.multiple, 'multiple'),
          props.className
        )}
      >
        {imageURL.length > 0 &&
          fileList.map((_, index) => (
            <div key={index} className={styles.preview}>
              <div
                style={{ backgroundImage: `url(${imageURL[index]})` }}
                className={styles.image}
                onClick={() => viewFile(imageURL[index])}
              />
              {!props.readOnly && (
                <button
                  type="button"
                  className={styles.delete}
                  onClick={() => deleteFile('image', index)}
                >
                  삭제
                </button>
              )}
            </div>
          ))}
        {videoURL.length > 0 &&
          fileList.map((_, index) => (
            <div key={index} className={styles.preview}>
              <div
                className="video_area"
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                }}
              >
                <video
                  src={videoURL[index]}
                  className={classnames(styles.video, 'excluded1', 'excluded2')}
                  controls
                  autoPlay
                  muted
                ></video>
              </div>
              {!props.readOnly && (
                <button
                  type="button"
                  className={styles.delete}
                  onClick={() => deleteFile('video', index)}
                >
                  삭제
                </button>
              )}
            </div>
          ))}
        {!props.readOnly ? (
          <>
            <input
              type="file"
              ref={fileInputRef}
              name={props.name}
              id={props.inputId}
              multiple={props.multiple || false}
              accept={acceptExt}
              className="hidden"
              onChange={(event) => changeFile(event)}
            />
            {((!props.multiple &&
              imageURL.length === 0 &&
              videoURL.length === 0) ||
              (props.multiple && imageURL.length < (props.maxLength || 4))) && (
              <label htmlFor={props.inputId} className={styles.label}>
                <i
                  className={classnames(
                    styles.uploader,
                    getStyles(props.acceptVideo, 'picture')
                  )}
                />
                upload file
              </label>
            )}
          </>
        ) : (
          !props.multiple && (
            <div className={styles.label}>
              {imageURL.length === 0 && videoURL.length === 0 && (
                <i
                  className={classnames(
                    styles.uploader,
                    getStyles(props.acceptVideo, 'picture')
                  )}
                />
              )}
            </div>
          )
        )}
      </div>
    </>
  );
});

FormMediaFile.displayName = 'FormMediaFile';

export default FormMediaFile;
